import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { ThemeContext } from 'styled-components'
import { useMediaLayout } from 'use-media'
import { useTranslation } from 'react-i18next'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'

import Button from 'components/atoms/button'
import LoadingIndicator from 'components/atoms/loading-indicator'
import Menu from 'components/organisms/menu'
import Modal from 'components/molecules/modal'
import PopupMenu from 'components/molecules/popup-menu'
import Icon from 'components/atoms/icon'

const StyledIcon = styled(Icon)`
  opacity: ${({ disabled }) => (disabled ? 0.45 : 1)};

  &:hover {
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  }

  svg {
    fill: transparent;

    g {
      stroke: ${({ theme }) => theme.colors.actionsStandardSecondary};
    }
  }
`

const StyledPopupMenu = styled(PopupMenu)`
  align-items: center;
  display: inline-flex;
  height: ${({ theme }) => theme.sizeByFactor(4.75)};

  .actual-menu {
    top: 0;
    right: 0;
    min-width: ${({ theme }) => theme.sizeByFactor(35)};

    div {
      width: 100%;
    }

    .actual-menu {
      position: relative;
      width: 100%;
      padding: 0 ${({ theme }) => theme.sizeByFactor(2)};
      box-shadow: none;
    }

    > p:first-child {
      font-size: ${({ theme }) => theme.sizeByFactor(1.75)};
      padding-left: ${({ theme }) => theme.sizeByFactor(2)};
      padding-right: ${({ theme }) => theme.sizeByFactor(2)};
    }
  }
`

const Trigger = styled.button`
  align-items: center;
  appearance: none;
  background: transparent;
  border: 0;
  display: inline-flex;
  height: ${({ theme }) => theme.sizeByFactor(4.75)};
  justify-content: center;
  padding: 0;
  width: ${({ theme }) => theme.sizeByFactor(3)};
`

const DesktopMenuWrapper = styled.div`
  align-items: center;
  display: inline-flex;
  height: ${({ theme }) => theme.sizeByFactor(4.75)};
`

const StyledMenu = styled(Menu)`
  div {
    position: relative;
    width: 100%;
  }

  .actual-menu {
    position: relative;
    width: 100%;
    padding: ${({ theme }) => theme.sizeByFactor(1)};
    box-shadow: none;
    right: 0;
    top: 0;
  }
`

const StyledTitle = styled.span`
  display: flex;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizeByFactor(1)};
  align-items: center;

  > span {
    width: auto !important;
  }
`

const StyledLoadingIndicator = styled(LoadingIndicator)`
  position: relative;
  top: -${({ theme }) => theme.sizeByFactor(0.25)};
`

const StyledCloseButton = styled(Button)`
  position: relative;
  margin-left: auto;
  top: -${({ theme }) => theme.sizeByFactor(0.25)};
  right: -${({ theme }) => theme.sizeByFactor(1.25)};

  > span {
    width: auto !important;
    height: auto !important;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  svg {
    width: ${({ theme }) => theme.sizeByFactor(1.925)};
    height: ${({ theme }) => theme.sizeByFactor(1.925)};
  }
`

const MediaFormContextMenu = ({
  selectedImageIds,
  onDelete,
  onDownloadZip,
  onCustomAction,
  customOptions,
  type,
  disabled,
  isSubmitting,
}) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)
  const isMobile = useMediaLayout({ maxWidth: theme.metrics.tablet - 1 })
  const [menuOpen, setMenuOpen] = useState(false)
  const hasSelectedItems = selectedImageIds && selectedImageIds.length > 0
  const triggerDisabled = !hasSelectedItems || disabled

  const handleOpenMenu = () => {
    if (triggerDisabled) {
      return
    }

    setMenuOpen(true)
  }

  const handleCloseMenu = () => {
    setMenuOpen(false)
  }

  const items = []

  if (customOptions && customOptions.length > 0) {
    customOptions.forEach((customAction) => {
      items.push({
        label: customAction.buttonText,
        icon: customAction.icon,
        disabled: !hasSelectedItems || disabled,
        onClick: () => {
          handleCloseMenu()
          customAction.handler()
        },
      })
    })
  }

  if (onDownloadZip) {
    items.push({
      label: t('downloadZip.downloadAsZip'),
      icon: 'download',
      disabled: triggerDisabled || isSubmitting,
      onClick: () => {
        handleCloseMenu()
        onDownloadZip()
      },
    })
  }

  if (onDelete) {
    items.push({
      label: t('delete'),
      icon: 'trashCan',
      disabled: triggerDisabled,
      onClick: () => {
        handleCloseMenu()
        onDelete()
      },
    })
  }

  if (items.length === 0) {
    return null
  }

  if (!isMobile) {
    return (
      <ClickAwayListener onClickAway={handleCloseMenu}>
        <DesktopMenuWrapper>
          <StyledPopupMenu
            title={
              <StyledTitle>
                {t('actions')}
                {isSubmitting && <StyledLoadingIndicator size="extraSmall" />}
                <StyledCloseButton
                  level="minimal"
                  icon="close"
                  iconColor="darkestBackground"
                  onClick={handleCloseMenu}
                />
              </StyledTitle>
            }
            items={items}
            positionMenu={{ toLeft: true }}
            isMenuActive={menuOpen}
            isMenuLoading={isSubmitting}
          >
            <Trigger
              type="button"
              disabled={triggerDisabled}
              onClick={handleOpenMenu}
            >
              <StyledIcon disabled={triggerDisabled} type="dots" tag="span" />
            </Trigger>
          </StyledPopupMenu>
        </DesktopMenuWrapper>
      </ClickAwayListener>
    )
  }

  return (
    <>
      <Trigger type="button" disabled={triggerDisabled} onClick={handleOpenMenu}>
        <StyledIcon disabled={triggerDisabled} type="dots" />
      </Trigger>
      <Modal
        title={
          <StyledTitle>
            {t('actions')}
            {isSubmitting && <StyledLoadingIndicator size="extraSmall" />}
          </StyledTitle>
        }
        showCloseButton
        open={menuOpen}
        closeHandler={handleCloseMenu}
      >
        <StyledMenu variant="popup" isMenuActive={menuOpen} items={items} />
      </Modal>
    </>
  )
}

MediaFormContextMenu.propTypes = {
  selectedImageIds: PropTypes.array,
  onDelete: PropTypes.func,
  onDownloadZip: PropTypes.func,
  onCustomAction: PropTypes.func,
  customOptions: PropTypes.arrayOf(
    PropTypes.shape({
      handler: PropTypes.func.isRequired,
      buttonText: PropTypes.string.isRequired,
      icon: PropTypes.string,
    }).isRequired,
  ),
  type: PropTypes.oneOf(['images', 'documents']),
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
}

MediaFormContextMenu.defaultProps = {
  selectedImageIds: [],
  onDelete: null,
  onDownloadZip: null,
  onCustomAction: null,
  customOptions: [],
  type: 'images',
  disabled: false,
  isSubmitting: false,
}

export default MediaFormContextMenu
